import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, Chip, CircularProgress, Divider, Paper, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import moment from "moment";
import React, { useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import CustomButton from "../Common/CustomButton";
import EditableMRTCell from "../Common/EditableMRTCell";
import EllipsisedCell from "../Common/EllipsisedCell";
import { LoggedInSkeleton } from "../Common/LoggedInSkeleton";
import { getReconTopBarButtons } from "../Common/TopNavBar";
import useFetch from "../Common/useFetch";
import { userContext } from "../Contexts/userContext";
import ReactTable from "../ReactTable";
import ClosingBalanceUploadFilter from "./ClosingBalanceUploadFilter";
import UploadFileCommonDialog, { downloadExcelFromBuffer } from "./UploadFileCommonDialog";

export type AllowedFilterKeysClosingBalance = "vendorName" | "panNumber";

export interface ClosingBalanceResponseDto {
  id: string;
  pan: string;
  entityName: string;
  vendorName: string;
  entityBalance: number;
  allEntityBalance: number;
  entityHold: number;
  bdpoHold: number;
  netBalance: number;
  remarks: string;
}

export default function ClosingBalanceUpload() {
  const { actor } = useContext(userContext);
  const location = useLocation<any>();

  const [openFilters, setOpenFilters] = React.useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = React.useState<Record<AllowedFilterKeysClosingBalance, string[]>>({
    vendorName: [],
    panNumber: [],
  });
  const [openUploadInvoiceModal, setOpenUploadInvoiceModal] = React.useState<boolean>(false);
  const [closingBalanceData, setClosingBalanceData] = React.useState<ClosingBalanceResponseDto[]>([]);
  const lastRefreshedAt = React.useRef<Date | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isDownloadingReport, setIsDownloadingReport] = React.useState<boolean>(false);

  const handleDownloadReport = async () => {
    setIsDownloadingReport(true);
    await useFetch("/closing-balance/report", "POST", {
      data: { vendorName: appliedFilters.vendorName || [], panNumber: appliedFilters.panNumber || [], anchorId: 2 },
      failureMessage: "Failed to download report",
      thenCallBack: (response) => {
        downloadExcelFromBuffer(response.data.result.cbSummaryReport, "closing-balance-report.xlsx");
        setIsDownloadingReport(false);
      },
      catchCallBack: () => {
        setIsDownloadingReport(false);
      },
    });
  };

  useEffect(() => {
    listAllData();
  }, [appliedFilters]);

  const listAllData = async () => {
    setIsLoading(true);
    await useFetch("/closing-balance", "POST", {
      failureMessage: "Failed to fetch closing balance data",
      data: {
        vendorName: appliedFilters.vendorName || [],
        pan: appliedFilters.panNumber || [],
        anchorId: 2,
      },
      thenCallBack: (response) => {
        setClosingBalanceData((response.data.result.closingBalances as ClosingBalanceResponseDto[]) || []);
        lastRefreshedAt.current = response.data.result.lastRefreshedAt;
        setIsLoading(false);
      },
      catchCallBack: () => {
        setIsLoading(false);
      },
    });
  };

  const handleEdit = async (id: string, newValue) => {
    let formattedValue = newValue;

    if (newValue === "") formattedValue = null;
    await useFetch(`/closing-balance/${id}/remarks`, "PUT", {
      data: { remarks: formattedValue },
      failureMessage: "Failed to update remarks",
    });
  };

  const columns = useMemo<MRT_ColumnDef<ClosingBalanceResponseDto>[]>(
    () => [
      {
        accessorKey: "vendorName",
        header: "Vendor Name",
        minSize: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "pan",
        header: "PAN",
        minSize: 150,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "entityBalance",
        header: "Scootsy Balance",
        filterVariant: "range",
        size: 350,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "allEntityBalance",
        header: "Closing Balance - All Entity",
        filterVariant: "range",
        size: 350,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "entityHold",
        header: "Entity Wise Hold",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "bdpoHold",
        header: "BDPO Hold",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "netBalance",
        header: "Net Balance",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "remarks",
        header: "CB Remarks",
        size: 350,
        Cell: ({ row }) => (
          <EditableMRTCell
            value={row.original.remarks}
            handleEdit={(newValue) => {
              row.original.remarks = newValue;
              handleEdit(row.original.id, newValue);
            }}
          />
        ),
      },
    ],
    []
  );
  return (
    <LoggedInSkeleton
      topBarButtons={getReconTopBarButtons("Upload", actor.name, location?.state?.openCollapseOb, actor)}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          p: 2,
          my: 2,
          backgroundColor: "#fff",
          borderRadius: 1,
          border: "1px solid #E5E5E5",
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Upload Closing Balance Summary
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload the daily closing balance summary to update vendor balance information. The file should contain PAN
            numbers, balances, and hold amounts.
          </Typography>
        </Box>
        <CustomButton variant="contained" onClick={() => setOpenUploadInvoiceModal(true)} sx={{ flexShrink: 0 }}>
          Upload file
        </CustomButton>
      </Box>
      <Paper
        elevation={1}
        sx={{
          borderRadius: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            direction: "row",
            justifyContent: "flex-start",
            gap: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Closing Balance Summary
          </Typography>
          <Chip
            label={
              lastRefreshedAt.current
                ? `Last refreshed at ${moment(lastRefreshedAt.current).format("DD/MM/YY h:mm A")} IST`
                : "Not refreshed yet"
            }
            sx={{
              color: "#0288D1",
              backgroundColor: "#E3F2FD",
              borderRadius: "4px",
              "& .MuiChip-label": { fontSize: "14px", fontWeight: 600 },
            }}
          />
        </Box>

        <Box display="flex" gap={2}>
          <CustomButton onClick={() => setOpenFilters(true)} variant="outlined" startIcon={<FilterListIcon />}>
            Filter
          </CustomButton>

          <CustomButton
            variant="outlined"
            onClick={handleDownloadReport}
            disabled={isDownloadingReport}
            startIcon={isDownloadingReport ? <CircularProgress size={20} /> : null}
          >
            Download Report
          </CustomButton>
        </Box>
      </Paper>
      <Divider />
      <ReactTable
        data={closingBalanceData}
        columns={columns}
        isLoading={isLoading}
        enableColumnOrdering={true}
        enableRowVirtualization={true}
        enableColumnVirtualization={true}
        additionalInitialState={{
          columnPinning: {
            left: ["vendorName"],
            right: [],
          },
        }}
      />
      {openFilters && (
        <ClosingBalanceUploadFilter
          open={openFilters}
          onClose={() => {
            setOpenFilters(false);
          }}
          setAppliedFilters={setAppliedFilters}
          appliedFilters={appliedFilters}
        />
      )}
      {openUploadInvoiceModal && (
        <UploadFileCommonDialog
          open={openUploadInvoiceModal}
          onClose={() => setOpenUploadInvoiceModal(false)}
          useCase="uploadClosingBalanceSummary"
          getAllData={listAllData}
        />
      )}
    </LoggedInSkeleton>
  );
}
